import { off, ref } from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import axios from "../lib/axios";
import { db } from "../lib/firebase";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";
import { LaunchError } from "../utils/errors";
import { getPathDatabaseByRole, renewToken } from "./auth";
import {
  ADS_CAMPAIGN_NOT_FOUND,
  MISSING_PARAMETERS,
  TOKEN_EXPIRED,
  TOKEN_INVALID,
} from "../i18n/keysTranslations";
import {
  getFirstReviewerUrl,
  normalizeApprovalReviewersMap,
} from "../components/ContentApproval/approvalReviewersUtils";

const buildCampaignApprovalUrl = (endpoint, token) => {
  const baseUrl = `${getCloudFunctionUrl(endpoint)}/${endpoint}`;
  if (!token) {
    return baseUrl;
  }

  return `${baseUrl}?token=${encodeURIComponent(token)}`;
};

const normalizeCampaignApprovalResponse = (result) => {
  return {
    ok: Boolean(result?.ok),
    ...(result?.data || {}),
    ...(result?.message ? { message: result.message } : {}),
    ...(result?.code ? { code: result.code } : {}),
  };
};

const getCampaignApprovalHandledError = (error) => {
  const responseData = error?.response?.data;
  const code = responseData?.code || error?.code;

  if (
    [
      ADS_CAMPAIGN_NOT_FOUND,
      MISSING_PARAMETERS,
      TOKEN_EXPIRED,
      TOKEN_INVALID,
      "approvalClosed",
      "Unauthorized",
    ].includes(code)
  ) {
    return {
      ok: false,
      code,
      message: responseData?.message || error?.message || "",
    };
  }

  return null;
};

const runCampaignApprovalBearerRequest = async ({
  dispatch,
  endpoint,
  data,
  functionName,
  showAlert = true,
}) => {
  try {
    const token = await dispatch(renewToken());

    if (!token) {
      return {
        ok: false,
        code: "missingAuthToken",
        message: "Missing auth token",
      };
    }

    const { data: result } = await axios({
      method: "post",
      url: buildCampaignApprovalUrl(endpoint),
      data,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return normalizeCampaignApprovalResponse(result);
  } catch (error) {
    const handledError = getCampaignApprovalHandledError(error);
    if (handledError) {
      return handledError;
    }

    const errorFormatted = dispatch(
      LaunchError(error, showAlert, [], {
        functionName,
      })
    );

    return {
      ok: false,
      code: errorFormatted?.code,
      message: errorFormatted?.message || error?.message || "",
    };
  }
};

export const startGetCampaignApprovalAdPreview =
  ({ token, campaignID, platform, entityID, adFormat }, signal) =>
  async (dispatch) => {
    try {
      const { data: result } = await axios({
        method: "post",
        url: buildCampaignApprovalUrl("getCampaignApprovalAdPreview", token),
        data: {
          campaignID,
          platform,
          entityID,
          adFormat,
        },
        signal,
      });

      return normalizeCampaignApprovalResponse(result);
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        return { ok: false, aborted: true };
      }

      const handledError = getCampaignApprovalHandledError(error);
      if (handledError) {
        return handledError;
      }

      const errorFormatted = dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetCampaignApprovalAdPreview",
        })
      );

      return {
        ok: false,
        code: errorFormatted?.code,
        message: errorFormatted?.message || error?.message || "",
      };
    }
  };

export const startGetCampaignApprovalPayload = (token) => async (dispatch) => {
  try {
    const { data: result } = await axios({
      method: "get",
      url: buildCampaignApprovalUrl("getCampaignApprovalPayload", token),
    });

    const normalized = normalizeCampaignApprovalResponse(result);
    if (!normalized?.ok) {
      return normalized;
    }

    const approval = normalizeCampaignApprovalState({
      ...(normalized.approval || {}),
      reviewers: normalized.approval?.reviewers || normalized.reviewers || {},
    });

    return {
      ...normalized,
      approval,
    };
  } catch (error) {
    const handledError = getCampaignApprovalHandledError(error);
    if (handledError) {
      return handledError;
    }

    const errorFormatted = dispatch(
      LaunchError(error, false, [], {
        functionName: "startGetCampaignApprovalPayload",
      })
    );

    return {
      ok: false,
      code: errorFormatted?.code,
      message: errorFormatted?.message || error?.message || "",
    };
  }
};

export const startSubmitCampaignApprovalComment =
  ({ token, type, body, target }) =>
  async (dispatch) => {
    try {
      const { data: result } = await axios({
        method: "post",
        url: buildCampaignApprovalUrl("submitCampaignApprovalComment", token),
        data: {
          type,
          body,
          ...(target ? { target } : {}),
        },
      });

      return normalizeCampaignApprovalResponse(result);
    } catch (error) {
      const handledError = getCampaignApprovalHandledError(error);
      if (handledError) {
        return handledError;
      }

      const errorFormatted = dispatch(
        LaunchError(error, false, [], {
          functionName: "startSubmitCampaignApprovalComment",
        })
      );

      return {
        ok: false,
        code: errorFormatted?.code,
        message: errorFormatted?.message || error?.message || "",
      };
    }
  };

export const startDecideCampaignApproval =
  ({ token, decision, body }) =>
  async (dispatch) => {
    try {
      const { data: result } = await axios({
        method: "post",
        url: buildCampaignApprovalUrl("decideCampaignApproval", token),
        data: {
          decision,
          body,
        },
      });

      return normalizeCampaignApprovalResponse(result);
    } catch (error) {
      const handledError = getCampaignApprovalHandledError(error);
      if (handledError) {
        return handledError;
      }

      const errorFormatted = dispatch(
        LaunchError(error, false, [], {
          functionName: "startDecideCampaignApproval",
        })
      );

      return {
        ok: false,
        code: errorFormatted?.code,
        message: errorFormatted?.message || error?.message || "",
      };
    }
  };

export const startEnableCampaignApproval =
  ({ campaignID }) =>
  async (dispatch) => {
    return runCampaignApprovalBearerRequest({
      dispatch,
      endpoint: "enableCampaignApproval",
      data: { campaignID },
      functionName: "startEnableCampaignApproval",
      showAlert: true,
    });
  };

export const startBumpCampaignApprovalDraft =
  ({ campaignID }) =>
  async (dispatch) => {
    return runCampaignApprovalBearerRequest({
      dispatch,
      endpoint: "bumpCampaignApprovalDraft",
      data: { campaignID },
      functionName: "startBumpCampaignApprovalDraft",
      showAlert: true,
    });
  };

export const normalizeCampaignApprovalState = (approvalData) => {
  if (!approvalData) {
    return {
      enabled: false,
      status: null,
      currentDraftID: null,
      draftRevision: 0,
      url: null,
      reviewers: [],
      comments: [],
    };
  }

  const reviewers = normalizeApprovalReviewersMap(approvalData?.reviewers);

  return {
    ...approvalData,
    enabled: Boolean(approvalData?.enabled),
    currentDraftID: approvalData?.currentDraftID || null,
    draftRevision: Number(approvalData?.draftRevision) || 0,
    url: getFirstReviewerUrl({
      reviewers: approvalData?.reviewers,
      fallbackUrl: approvalData?.url || null,
    }),
    reviewers,
    comments: Object.entries(approvalData?.comments || {}).map(
      ([id, comment]) => ({
        id,
        ...comment,
      })
    ),
  };
};

export const startListenerCampaignApproval =
  ({
    campaignID,
    onUpdatedDataCallback = () => {},
    onReferenceAvailable = () => {},
  }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());

      if (!userID || !campaignID) {
        onUpdatedDataCallback(normalizeCampaignApprovalState(null));
        return false;
      }

      const dbRef = ref(db, `adsCampaignApprovals/${userID}/${campaignID}`);
      onReferenceAvailable(dbRef);
      onValue(dbRef, (snapshot) => {
        onUpdatedDataCallback(normalizeCampaignApprovalState(snapshot.val()));
      });

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerCampaignApproval",
          context: { campaignID },
          tags: { action: "listenerCampaignApproval" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerCampaignApproval = (listenerRef) => async () => {
  if (!listenerRef) {
    return;
  }

  await off(listenerRef);
};
